import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";

const DISMISSED = "operativo-install-dismissed";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);

export const InstallPrompt = () => {
  const { t } = useI18n();
  const [prompt, setPrompt] = useState(null);
  const [manual, setManual] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISSED) || isStandalone()) return;
    if (window.__opInstallPrompt) setPrompt(window.__opInstallPrompt);
    const onReady = () => setPrompt(window.__opInstallPrompt);
    const onPrompt = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    window.addEventListener("op-install-ready", onReady);
    window.addEventListener("beforeinstallprompt", onPrompt);
    // iOS non supporta beforeinstallprompt: servono le istruzioni manuali.
    const timer = setTimeout(() => {
      if (!window.__opInstallPrompt && isIos()) setManual(true);
    }, 2500);
    return () => {
      window.removeEventListener("op-install-ready", onReady);
      window.removeEventListener("beforeinstallprompt", onPrompt);
      clearTimeout(timer);
    };
  }, []);

  if (!prompt && !manual) return null;

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    await prompt.userChoice;
    window.__opInstallPrompt = null;
    setPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED, "1");
    setPrompt(null);
    setManual(false);
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 z-50 w-[min(26rem,calc(100vw-2rem))] -translate-x-1/2 rounded-xl border border-slate-800 bg-slate-950 p-4 text-slate-100 shadow-2xl"
      data-testid="install-prompt"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-indigo-500/20">
          <Download className="h-4 w-4 text-indigo-300" />
        </span>
        <div className="flex-1">
          <p className="text-sm font-semibold">{t("pwa.title")}</p>
          <p className="mt-1 text-xs text-slate-400">
            {manual ? t("pwa.iosSteps") : t("pwa.text")}
          </p>
          <div className="mt-3 flex gap-2">
            {manual ? (
              <span
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 px-3 py-2 text-xs font-semibold text-slate-200"
                data-testid="install-ios-hint"
              >
                <Share className="h-3.5 w-3.5" /> {t("pwa.iosShare")}
              </span>
            ) : (
              <Button
                data-testid="install-accept"
                onClick={install}
                className="h-9 bg-indigo-600 px-4 text-sm text-white hover:bg-indigo-500"
              >
                {t("pwa.cta")}
              </Button>
            )}
            <Button
              data-testid="install-dismiss"
              onClick={dismiss}
              variant="ghost"
              className="h-9 px-3 text-sm text-slate-400 hover:bg-slate-800 hover:text-white"
            >
              {t("pwa.later")}
            </Button>
          </div>
        </div>
        <button
          data-testid="install-close"
          onClick={dismiss}
          aria-label={t("pwa.later")}
          className="text-slate-500 transition-colors hover:text-slate-200"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
